// GPS positions as stored are not uniform: the app stores plain decimals ("55.47905" in
// PosGPSLatitude, "9.81946" in PosGPSLongitude), while most old trials have the old system's
// "E:09,42492" in PosGPSLatitude and "N:55,61717" in PosGPSLongitude - letter-prefixed, with a
// decimal comma, and the two the other way round. These read either kind, and show a position the
// same way everywhere: "55.47905, 9.81946" (latitude, longitude).

/** One stored coordinate: its number, and which axis its N/S/E/W prefix says it is (or null). */
export function parseCoordinate(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  if (text === '') return null;
  const m = text.match(/^([NSEW])?\s*:?\s*(-?\d+(?:[.,]\d+)?)\s*([NSEW])?$/i);
  if (!m) return null;
  const letter = (m[1] || m[3] || '').toUpperCase();
  let number = parseFloat(m[2].replace(',', '.'));
  if (Number.isNaN(number)) return null;
  if (letter === 'S' || letter === 'W') number = -Math.abs(number);
  const axis = letter === 'N' || letter === 'S' ? 'lat' : letter === 'E' || letter === 'W' ? 'lng' : null;
  return { number, axis };
}

/**
 * The position of a trial from its two GPS fields, whichever way they are stored, or null when it
 * has none (or it is not a valid position).
 * @returns {{ lat: number, lng: number } | null}
 */
export function parseGps(latitudeField, longitudeField) {
  const a = parseCoordinate(latitudeField);
  const b = parseCoordinate(longitudeField);
  if (!a || !b) return null;
  let lat = a.number;
  let lng = b.number;
  // Prefixed values say which is which (old trials have them the other way round)
  if (a.axis === 'lng' || b.axis === 'lat') {
    lat = b.number;
    lng = a.number;
  }
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

/**
 * The two GPS fields as stored, each in its own axis by the rule parseGps uses (a prefix says which is which; old
 * trials have them the other way round): { latitude, longitude, swapped }. The values are not changed.
 */
export function gpsFieldsByAxis(latitudeField, longitudeField) {
  const a = parseCoordinate(latitudeField);
  const b = parseCoordinate(longitudeField);
  const swapped = a?.axis === 'lng' || b?.axis === 'lat';
  return swapped
    ? { latitude: longitudeField ?? null, longitude: latitudeField ?? null, swapped }
    : { latitude: latitudeField ?? null, longitude: longitudeField ?? null, swapped };
}

/** A trial's position as shown: "55.47905, 9.81946" (latitude, longitude); "" when it has none. */
export function formatGps(latitudeField, longitudeField) {
  const position = parseGps(latitudeField, longitudeField);
  return position ? `${position.lat.toFixed(5)}, ${position.lng.toFixed(5)}` : '';
}
